import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { Refresh as RefreshIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { visitsService } from '@services/visitsService';

interface ClinicVisitRow {
  id: string;
  patientName: string;
  doctorName: string;
  status: string;
  visitDate: string;
  cost: number;
}

const VisitsManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState<ClinicVisitRow[]>([]);

  const loadVisits = useCallback(async () => {
    if (!user?.clinicId) return;
    setLoading(true);
    setError(null);
    try {
      const visits = await visitsService.getVisitsForClinic(user.clinicId, 100);
      const mapped: ClinicVisitRow[] = visits.map((v: any) => ({
        id: v.id,
        patientName: v.patient_name || 'Pacient necunoscut',
        doctorName: `${v.vet_first_name || ''} ${v.vet_last_name || ''}`.trim() || 'Medic',
        status: v.status || 'draft',
        visitDate: v.visit_date || v.created_at,
        cost: Number(v.cost || 0)
      }));
      setRows(mapped);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Eroare la incarcarea vizitelor');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user?.clinicId]);

  useEffect(() => {
    void loadVisits();
  }, [loadVisits]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.patientName.toLowerCase().includes(q) ||
      r.doctorName.toLowerCase().includes(q) ||
      r.status.toLowerCase().includes(q)
    );
  }, [rows, searchTerm]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Vizite Medicale
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Lista reala a consultarilor din clinica. Pentru lucru clinic, deschide encounter-ul vizitei.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Actualizeaza">
            <IconButton onClick={loadVisits} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            disabled
            onClick={() =>
              setInfo('Crearea vizitei din acest ecran este dezactivata. Foloseste fluxul de programari/Start Visit.')
            }
          >
            Vizita Noua
          </Button>
        </Stack>
      </Box>

      {info && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Cauta pacient, medic sau status..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Pacient</TableCell>
                    <TableCell>Medic</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Cost</TableCell>
                    <TableCell>Actiuni</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.patientName}</TableCell>
                      <TableCell>{row.doctorName}</TableCell>
                      <TableCell>{new Date(row.visitDate).toLocaleString('ro-RO')}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.status} />
                      </TableCell>
                      <TableCell>{row.cost.toFixed(2)} RON</TableCell>
                      <TableCell>
                        <Tooltip title="Deschide encounter">
                          <IconButton onClick={() => navigate(`/visits/${row.id}/encounter`)}>
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary" align="center">
                          Nu exista vizite pentru filtrele selectate.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VisitsManagement;
